import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSync } from '@fortawesome/free-solid-svg-icons';
import { DynamicButton } from './basic/dynamicButton';

type ImageCropperProps = {
  isOpen: boolean,
  imageUrl: string,
  imageMaxWidth?: number,
  imageMaxHeight?: number,
  imageRatioWidth?: number,
  imageRatioHeight?: number,
  onClose: () => any,
  onCrop: (imageData: string) => any,
  onChangeImage: () => any
}

type CropMoveDetails = {
  startX: number,
  startY: number,
  startCropRect: Rect
};

type CropOptions = {
  maxWidth?: number,
  maxHeight?: number,
  aspectRatioWidth?: number,
  aspectRatioHeight?: number,
};

class Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;

  public constructor(init?: Partial<Rect>) {
    this.left = 0;
    this.top = 0;
    this.right = 0;
    this.bottom = 0;

    Object.assign(this, init);
  }

  width() {
    return this.right - this.left;
  }

  height() {
    return this.bottom - this.top;
  }

  translateLeft(offset: number, anchorRight: boolean, minSize: number, boundsWidth: number, maxSize?: number) {
    if (!maxSize) {
      maxSize = boundsWidth;
    }

    const result = new Rect(this);
    const coercedSize = Math.max(minSize, Math.min(maxSize, this.width()));
    const minLeft = anchorRight ? this.right - maxSize : 0;
    const maxLeft = anchorRight ? this.right - minSize : boundsWidth - coercedSize;

    result.left = Math.max(minLeft, Math.min(maxLeft, this.left + offset));
    result.right = Math.min(boundsWidth, !anchorRight ? result.left + coercedSize : this.right);

    return result;
  }

  translateTop(offset: number, anchorBottom: boolean, minSize: number, boundsHeight: number, maxSize?: number) {
    if (!maxSize) {
      maxSize = boundsHeight;
    }

    const result = new Rect(this);
    const coercedSize = Math.max(minSize, Math.min(maxSize, this.height()));
    const minTop = anchorBottom ? this.bottom - maxSize : 0;
    const maxTop = anchorBottom ? this.bottom - minSize : boundsHeight - coercedSize;

    result.top = Math.max(minTop, Math.min(maxTop, this.top + offset));
    result.bottom = Math.min(boundsHeight, !anchorBottom ? result.top + coercedSize : this.bottom);

    return result;
  }

  translateRight(change: number, minSize: number, boundsWidth: number, maxSize?: number) {
    const result = new Rect(this);

    result.left = Math.max(0, this.left);
    result.right = Math.max(result.left + minSize, Math.min(boundsWidth, this.right + change));

    if (maxSize) {
      result.right = Math.min(result.left + maxSize, result.right);
    }

    return result;
  }

  translateBottom(change: number, minSize: number, boundsHeight: number, maxSize?: number) {
    const result = new Rect(this);

    result.top = Math.max(0, this.top);
    result.bottom = Math.max(result.top + minSize, Math.min(boundsHeight, this.bottom + change));

    if (maxSize) {
      result.bottom = Math.min(result.top + maxSize, result.bottom);
    }

    return result;
  }

  ensureBounds(cropOptions: CropOptions, boundsWidth: number, boundsHeight: number, anchorLeft: boolean = true, anchorTop: boolean = true) {
    const result = new Rect();
    result.left = Math.max(0, Math.min(boundsWidth, this.left));
    result.right = Math.min(result.left + (cropOptions.maxWidth || boundsWidth), Math.max(0, Math.min(boundsWidth, this.right)));
    result.top = Math.max(0, Math.min(boundsHeight, this.top));
    result.bottom = Math.min(result.top + (cropOptions.maxHeight || boundsHeight), Math.max(0, Math.min(boundsHeight, this.bottom)));

    // ensure aspect ratio
    if (cropOptions.aspectRatioWidth && cropOptions.aspectRatioHeight) {
      const currentRatio = result.width() / result.height();
      const expectedRatio = cropOptions.aspectRatioWidth / cropOptions.aspectRatioHeight;

      if (Math.abs(currentRatio - expectedRatio) > 0.001) {
        if (currentRatio > expectedRatio) {
          // fix width
          if (anchorLeft) {
            result.right = result.left + result.height() * expectedRatio;
          }
          else {
            result.left = result.right - result.height() * expectedRatio;
          }
        }
        else {
          // fix height
          if (anchorTop) {
            result.bottom = result.top + result.width() / expectedRatio;
          }
          else {
            result.top = result.bottom - result.width() / expectedRatio;
          }
        }
      }
    }
    
    return result;
  }

  moveTopLeftCorner(newLeft: number, newTop: number, minSize: number, boundsWidth: number, boundsHeight: number, cropOptions: CropOptions) {
    return new Rect(this)
      .translateLeft(newLeft - this.left, true, minSize, boundsWidth, cropOptions.maxWidth)
      .translateTop(newTop - this.top, true, minSize, boundsHeight, cropOptions.maxHeight)
      .ensureBounds(cropOptions, boundsWidth, boundsHeight, false, false);
  }

  moveTopRightCorner(newRight: number, newTop: number, minSize: number, boundsWidth: number, boundsHeight: number, cropOptions: CropOptions) {
    return new Rect(this)
      .translateRight(newRight - this.right, minSize, boundsWidth, cropOptions.maxWidth)
      .translateTop(newTop - this.top, true, minSize, boundsHeight, cropOptions.maxHeight)
      .ensureBounds(cropOptions, boundsWidth, boundsHeight, true, false);
  }

  moveBottomLeftCorner(newLeft: number, newBottom: number, minSize: number, boundsWidth: number, boundsHeight: number, cropOptions: CropOptions) {
    return new Rect(this)
      .translateLeft(newLeft - this.left, true, minSize, boundsWidth, cropOptions.maxWidth)
      .translateBottom(newBottom - this.bottom, minSize, boundsHeight, cropOptions.maxHeight)
      .ensureBounds(cropOptions, boundsWidth, boundsHeight, false, true);
  }

  moveBottomRightCorner(newRight: number, newBottom: number, minSize: number, boundsWidth: number, boundsHeight: number, cropOptions: CropOptions) {
    return new Rect(this)
      .translateRight(newRight - this.right, minSize, boundsWidth, cropOptions.maxWidth)
      .translateBottom(newBottom - this.bottom, minSize, boundsHeight, cropOptions.maxHeight)
      .ensureBounds(cropOptions, boundsWidth, boundsHeight, true, true);
  }

  move(newLeft: number, newTop: number, minSize: number, boundsWidth: number, boundsHeight: number, cropOptions: CropOptions) {
    return new Rect(this)
      .translateLeft(newLeft - this.left, false, minSize, boundsWidth, cropOptions.maxWidth)
      .translateTop(newTop - this.top, false, minSize, boundsHeight, cropOptions.maxHeight)
      .ensureBounds(cropOptions, boundsWidth, boundsHeight);
  }

  transform(offsetX: number, offsetY: number, scale: number) {
    return new Rect({
      left: offsetX + this.left * scale,
      top: offsetY + this.top * scale,
      right: offsetX + this.right * scale,
      bottom: offsetY + this.bottom * scale
    });
  }
}

function cropImage(image: HTMLImageElement, crop: Rect): string {
  const canvas = document.createElement('canvas') as HTMLCanvasElement;
  const ctx = canvas.getContext('2d');
  
  canvas.width = crop.width();
  canvas.height = crop.height();

  ctx.drawImage(image, crop.left, crop.top, crop.width(), crop.height(), 0, 0, crop.width(), crop.height());

  const imageData = canvas.toDataURL('image/jpg', 0.9);

  canvas.remove();

  return imageData;
}

function renderCanvasCropMask(
  ctx: CanvasRenderingContext2D,
  gripSize: number,
  imageDisplayRect: Rect,
  cropDisplayRect: Rect
  ) {
  // fill outside crop area
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  if (cropDisplayRect.left > imageDisplayRect.left) {
    ctx.fillRect(
      imageDisplayRect.left,
      imageDisplayRect.top,
      cropDisplayRect.left - imageDisplayRect.left,
      imageDisplayRect.height());
  }
  if (cropDisplayRect.right < imageDisplayRect.right) {
    ctx.fillRect(
      cropDisplayRect.right,
      imageDisplayRect.top,
      imageDisplayRect.right - cropDisplayRect.right,
      imageDisplayRect.height());
  }
  if (cropDisplayRect.top > 0) {
    ctx.fillRect(
      cropDisplayRect.left,
      imageDisplayRect.top,
      cropDisplayRect.width(),
      cropDisplayRect.top - imageDisplayRect.top);
  }
  if (cropDisplayRect.bottom < imageDisplayRect.bottom) {
    ctx.fillRect(
      cropDisplayRect.left,
      cropDisplayRect.bottom,
      cropDisplayRect.width(),
      imageDisplayRect.bottom - cropDisplayRect.bottom);
  }

  // draw crop bounds
  ctx.strokeStyle = '#E82749';
  ctx.lineWidth = 3;
  ctx.setLineDash([gripSize, gripSize]);
  ctx.beginPath();

  ctx.moveTo(cropDisplayRect.left, cropDisplayRect.top + gripSize);
  ctx.lineTo(cropDisplayRect.left, cropDisplayRect.bottom - gripSize);

  ctx.moveTo(cropDisplayRect.left + gripSize, cropDisplayRect.top);
  ctx.lineTo(cropDisplayRect.right - gripSize, cropDisplayRect.top);

  ctx.moveTo(cropDisplayRect.right, cropDisplayRect.top + gripSize);
  ctx.lineTo(cropDisplayRect.right, cropDisplayRect.bottom - gripSize);

  ctx.moveTo(cropDisplayRect.left + gripSize, cropDisplayRect.bottom);
  ctx.lineTo(cropDisplayRect.right - gripSize, cropDisplayRect.bottom);

  ctx.stroke();
}

function computeCanvasScale(image: HTMLImageElement, canvas: HTMLCanvasElement, padding: number) {
  let scaleX = image.width > (canvas.offsetWidth - 2*padding) ? (canvas.offsetWidth - 2*padding) / image.naturalWidth : 1;
  let scaleY = image.height > (canvas.offsetHeight - 2*padding) ? (canvas.offsetHeight - 2*padding) / image.naturalHeight : 1;

  return Math.min(scaleX, scaleY);
}

function computeImageDisplayRect(image: HTMLImageElement, canvas: HTMLCanvasElement, scale: number) {
  const imageDisplayWidth = scale * image.naturalWidth;
  const imageDisplayHeight = scale * image.naturalHeight;
  const imageDisplayRect = new Rect();
  imageDisplayRect.left = (canvas.width - imageDisplayWidth) / 2;
  imageDisplayRect.top = (canvas.height - imageDisplayHeight) / 2;
  imageDisplayRect.right = imageDisplayRect.left + imageDisplayWidth;
  imageDisplayRect.bottom = imageDisplayRect.top + imageDisplayHeight;
  return imageDisplayRect;
}

function GripsElement({
  canvasRef,
  gripSize,
  imageScale,
  imageDisplayRect,
  imageWidth,
  imageHeight,
  cropRect,
  cropOptions,
  setCropRect
} : {
  canvasRef: React.MutableRefObject<HTMLCanvasElement>,
  gripSize: number,
  imageScale: number,
  imageDisplayRect: Rect,
  imageWidth: number,
  imageHeight: number,
  cropRect: Rect,
  cropOptions: CropOptions,
  setCropRect: (cropRect: Rect) => any
}) {
  const currentGripRef = useRef<HTMLDivElement>();
  const cropMoveDetails = useRef<CropMoveDetails>(null);
  const cropRectRef = useRef<Rect>(cropRect);
  const imageDisplayRectRef = useRef<Rect>(imageDisplayRect);
  const imageScaleRef = useRef<number>(imageScale);
  const imageWidthRef = useRef<number>(imageScale);
  const imageHeightRef = useRef<number>(imageScale);
  const minSize = 50;

  useEffect(() => { cropRectRef.current = cropRect; }, [cropRect]);
  useEffect(() => { imageDisplayRectRef.current = imageDisplayRect; }, [imageDisplayRect]);
  useEffect(() => { imageScaleRef.current = imageScale; }, [imageScale]);
  useEffect(() => { imageWidthRef.current = imageWidth; }, [imageWidth]);
  useEffect(() => { imageHeightRef.current = imageHeight; }, [imageHeight]);

  const gripMouseDownCallback = useCallback<React.MouseEventHandler<HTMLDivElement>>(ev => {
    currentGripRef.current = ev.currentTarget;
    cropMoveDetails.current = {
      startX: ev.clientX,
      startY: ev.clientY,
      startCropRect: cropRect
    };
  }, [cropRect]);
  
  const gripMouseUpCallback = useCallback(() => {
    currentGripRef.current = null;
    cropMoveDetails.current = null;
  }, []);

  const gripMouseMoveCallback = useCallback((ev: MouseEvent) => {
    const grip = currentGripRef.current;

    if (!grip) {
      return;
    }

    ev.preventDefault();

    const canvas = canvasRef.current;
    const cropRect = cropRectRef.current;
    const imageDisplayRect = imageDisplayRectRef.current;
    const imageScale = imageScaleRef.current;
    const imageWidth = imageWidthRef.current;
    const imageHeight = imageHeightRef.current;

    const canvasRect = canvas.getBoundingClientRect();
    const relativeX = ev.clientX - canvasRect.left;
    const relativeY = ev.clientY - canvasRect.top;
    const x = Math.min(imageDisplayRect.right, Math.max(imageDisplayRect.left, relativeX)) - imageDisplayRect.left;
    const y = Math.min(imageDisplayRect.bottom, Math.max(imageDisplayRect.top, relativeY)) - imageDisplayRect.top;
    const scaledX = x / imageScale;
    const scaledY = y / imageScale;

    let newCropRect = new Rect(cropRect);

    const action = grip.dataset.action;

    if (action === 'resize-top-left') {
      newCropRect = newCropRect.moveTopLeftCorner(scaledX, scaledY, minSize, imageWidth, imageHeight, cropOptions);
    }
    else if (action === 'resize-top-right') {
      newCropRect = newCropRect.moveTopRightCorner(scaledX, scaledY, minSize, imageWidth, imageHeight, cropOptions);
    }
    else if (action === 'resize-bottom-left') {
      newCropRect = newCropRect.moveBottomLeftCorner(scaledX, scaledY, minSize, imageWidth, imageHeight, cropOptions);
    }
    else if (action === 'resize-bottom-right') {
      newCropRect = newCropRect.moveBottomRightCorner(scaledX, scaledY, minSize, imageWidth, imageHeight, cropOptions);
    }
    else if (action === 'move') {
      if (cropMoveDetails.current) {
        const changeX = ev.clientX - cropMoveDetails.current.startX;
        const changeY = ev.clientY - cropMoveDetails.current.startY;

        const scaledChangeX = changeX / imageScale;
        const scaledChangeY = changeY / imageScale;

        newCropRect = newCropRect.move(
          cropMoveDetails.current.startCropRect.left + scaledChangeX,
          cropMoveDetails.current.startCropRect.top + scaledChangeY,
          minSize,
          imageWidth,
          imageHeight,
          cropOptions);
      }
    }

    if (newCropRect) {
      setCropRect(newCropRect);
    }
  }, [imageDisplayRectRef.current, cropRectRef.current]);

  useEffect(() => {
    document.addEventListener('mousemove', gripMouseMoveCallback);
    document.addEventListener('mouseup', gripMouseUpCallback);

    return () => {
      document.removeEventListener('mousemove', gripMouseMoveCallback);
      document.removeEventListener('mouseup', gripMouseUpCallback);
    };
  }, []);

  const cropDisplayRect = (cropRect || new Rect()).transform(imageDisplayRect.left, imageDisplayRect.top, imageScale);

  return (
    <>
      {/* Move grip */}
      <div
        data-action="move"
        className="absolute border-red-light bg-transparent cursor-move"
        style={{
          width: `${cropDisplayRect.width()}px`,
          height: `${cropDisplayRect.height()}px`,
          left: `${cropDisplayRect.left}px`,
          top: `${cropDisplayRect.top}px`
        }}
        onMouseDown={gripMouseDownCallback}
        />
      {/* Top-left grip */}
      <div
        data-action="resize-top-left"
        className="absolute border-red-light bg-transparent"
        style={{
          cursor: 'nw-resize',
          borderWidth: '3px',
          width: `${gripSize}px`,
          height: `${gripSize}px`,
          left: `${cropDisplayRect.left - gripSize/2}px`,
          top: `${cropDisplayRect.top - gripSize/2}px`
        }}
        onMouseDown={gripMouseDownCallback}
        />
      {/* Top-right grip */}
      <div
        data-action="resize-top-right"
        className="absolute border-red-light bg-transparent"
        style={{
          cursor: 'ne-resize',
          borderWidth: '3px',
          width: `${gripSize}px`,
          height: `${gripSize}px`,
          left: `${cropDisplayRect.right - gripSize/2}px`,
          top: `${cropDisplayRect.top - gripSize/2}px`
        }}
        onMouseDown={gripMouseDownCallback}
        />
      {/* Bottom-left grip */}
      <div
        data-action="resize-bottom-left"
        className="absolute border-red-light bg-transparent"
        style={{
          cursor: 'sw-resize',
          borderWidth: '3px',
          width: `${gripSize}px`,
          height: `${gripSize}px`,
          left: `${cropDisplayRect.left - gripSize/2}px`,
          top: `${cropDisplayRect.bottom - gripSize/2}px`
        }}
        onMouseDown={gripMouseDownCallback}
        />
      {/* Bottom-right grip */}
      <div
        data-action="resize-bottom-right"
        className="absolute border-red-light bg-transparent"
        style={{
          cursor: 'se-resize',
          borderWidth: '3px',
          width: `${gripSize}px`,
          height: `${gripSize}px`,
          left: `${cropDisplayRect.right - gripSize/2}px`,
          top: `${cropDisplayRect.bottom - gripSize/2}px`
        }}
        onMouseDown={gripMouseDownCallback}
        />
    </>
  );
}

function CropCanvas({
  image,
  cropRect,
  cropOptions,
  gripSize = 10,
  setCropRect
}: {
  image: HTMLImageElement,
  cropRect: Rect,
  cropOptions: CropOptions,
  gripSize: number,
  setCropRect: (cropRect: Rect) => void
}) {
  const [imageScale, setImageScale] = useState<number>(1);
  const [imageDisplayRect, setImageDisplayRect] = useState<Rect>(new Rect());
  const canvasRef = useRef<HTMLCanvasElement>();
  const refreshCanvasRef = useRef<() => any>();

  const refreshCanvasCallback = useCallback(() => {
    if (!image || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    
    const canvasPadding = 4;
    const scale = computeCanvasScale(image, canvas, canvasPadding);
    const imageDisplayRect = computeImageDisplayRect(image, canvas, scale);

    setImageDisplayRect(imageDisplayRect);
    setImageScale(scale);

    if (!cropRect) {
      setCropRect(new Rect({
        left: 0,
        top: 0,
        right: cropOptions.maxWidth ? Math.min(image.naturalWidth, cropOptions.maxWidth) : image.naturalWidth,
        bottom: cropOptions.maxHeight ? Math.min(image.naturalHeight, cropOptions.maxHeight) : image.naturalHeight
      }).ensureBounds(cropOptions, image.naturalWidth, image.naturalHeight));
    }

    if (cropRect) {
      const cropDisplayRect = cropRect.transform(imageDisplayRect.left, imageDisplayRect.top, scale);
      const ctx = canvas.getContext('2d');

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, imageDisplayRect.left, imageDisplayRect.top, imageDisplayRect.width(), imageDisplayRect.height());

      renderCanvasCropMask(
        ctx,
        gripSize,
        imageDisplayRect,
        cropDisplayRect
      );
    }
  }, [
    canvasRef.current,
    cropRect,
    image,
    gripSize,
    setCropRect]);

  const resizeHandler = useCallback(() => {
    refreshCanvasRef.current?.();
  }, []);

  useEffect(() => {
    refreshCanvasRef.current = refreshCanvasCallback;
  }, [refreshCanvasCallback]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    
    refreshCanvasCallback();

    return () => {
      window.removeEventListener('resize', resizeHandler);
    }
  }, []);

  useEffect(() => {
    setCropRect(null);

    if (image) {
      refreshCanvasRef.current?.();
    }
  }, [image]);

  useEffect(() => {
    if (!!image) {
      refreshCanvasCallback();
    }
  }, [cropRect])

  return (
    <div className="absolute inset-4">
      <canvas ref={canvasRef} className="absolute left-0 top-0 w-full h-full" />

      <GripsElement
        canvasRef={canvasRef}
        gripSize={gripSize}
        cropRect={cropRect}
        cropOptions={cropOptions}
        imageScale={imageScale}
        imageDisplayRect={imageDisplayRect}
        imageWidth={image?.naturalWidth || 0}
        imageHeight={image?.naturalHeight || 0}
        setCropRect={setCropRect} />
    </div>
  );
}

export function ImageCropper({
    isOpen,
    imageUrl,
    imageMaxWidth,
    imageMaxHeight,
    imageRatioWidth,
    imageRatioHeight,
    onClose,
    onCrop,
    onChangeImage
  } : ImageCropperProps) {
    const [cropRect, setCropRect] = useState<Rect>(null);
    const [image, setImage] = useState<HTMLImageElement>(null);
    const cropOptions = useMemo<CropOptions>(() => ({
      maxWidth: imageMaxWidth,
      maxHeight: imageMaxHeight,
      aspectRatioWidth: imageRatioWidth,
      aspectRatioHeight: imageRatioHeight
    }), [imageMaxWidth, imageMaxHeight, imageRatioWidth, imageRatioHeight])

    const closeCallback = useCallback(() => onClose(), [onClose]);
    const cropCallback = useCallback(() => {
      if (image) {
        const imageData = cropImage(image, cropRect);
        onCrop(imageData);
      }
    }, [image, cropRect, onCrop]);

    useEffect(() => {
      if (!isOpen) {
        setCropRect(null);
      }
    }, [isOpen]);

    useEffect(() => {
      setImage(null);

      const image = new Image();
      image.src = imageUrl;
      image.onload = () => setImage(image);
    }, [imageUrl]);

    if (!isOpen) {
      return null;
    }

    return (
      <Modal
        open={isOpen}
        onClose={closeCallback}
        >
        <div className="flex flex-col absolute top-0 left-0 w-full h-full outline-none bg-navy-blue text-white">
          {/* Header */}
          <div className="border-b-1 border-white border-opacity-20">
            <div className="flex justify-between p-3 mx-auto" style={{ maxWidth: '1256px' }}>
              <div className="text-2xl font-bold">Crop image</div>

              <div
                className="flex items-center justify-center rounded-full bg-white bg-opacity-50 text-white w-10 h-10 cursor-pointer hover:bg-opacity-100 hover:bg-red-light"
                onClick={() => onClose()}
                >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </div>
            </div>
          </div>
          
          {/* Image */}
          <div className="relative flex-1">
            <CropCanvas
              image={image}
              cropRect={cropRect}
              gripSize={10}
              cropOptions={cropOptions}
              setCropRect={setCropRect} />
          </div>

          {/* Footer */}
          <div>
            <div className="flex justify-center items-center p-3 mx-auto" style={{ maxWidth: '1256px' }}>
              <DynamicButton
                layout="px-2 py-1 font-bold rounded-lg hover:bg-white hover:text-navy-light cursor-pointer disabled:bg-navy-light outline-none focus:outline-none"
                onClick={onChangeImage}
                >
                <FontAwesomeIcon icon={faSync} size="lg" />
                <span className="ml-2">Change image</span>
              </DynamicButton>
              <DynamicButton
                layout="bg-red-light px-16 py-3 ml-24 font-bold rounded-lg hover:bg-white hover:text-navy-light cursor-pointer disabled:bg-navy-light outline-none focus:outline-none"
                onClick={cropCallback}
                >
                Crop
              </DynamicButton>
            </div>
          </div>
        </div>
      </Modal>
    );
}